/**
 * Finance summary scoped to ONE entity (horse OR owner).
 *
 * Built as a sibling to FinanceSummaryPage.js — same visual identity (KPIs,
 * service cards, competition section), but with an entity picker at the top.
 * The data comes from a dedicated backend endpoint:
 *
 *   GET /accounting/finance/entity-totals?from=...&to=...&horse_id|owner_id=...
 *
 * That endpoint scopes every aggregate (vetclinic, farrier, transport,
 * accommodation, competition) to either the single selected horse, or to all
 * horses owned by the selected owner.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonIcon from '@mui/icons-material/Person';
import PetsIcon from '@mui/icons-material/Pets';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
// ---------------------------------------------------------------------------
// 📦 XLSX library
// ---------------------------------------------------------------------------
// We use `xlsx-js-style` — a drop-in fork of SheetJS Community that actually
// honours cell styles (fills, borders, fonts, number formats) on write. The
// plain `xlsx` v0.18 silently drops every `.s` property so the premium
// formatting below would otherwise vanish into a plain text grid.
//
// API is identical to `xlsx`, so swapping the import is the only change
// required to get colourful output.
// ---------------------------------------------------------------------------
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import DateRange from './components/DateRange';
import KPI from './components/KPI';
import { SMART_COLORS as COLORS, smartCardSx as cardSx, smartTableHeadSx } from '../../theme/smartStableTheme';
import { formatDateDisplay, formatDateInput } from '../../utils/dateUtils';

// ---------------------------------------------------------------------------
// Same service metadata as FinanceSummaryPage so labels stay consistent.
// ---------------------------------------------------------------------------
const SERVICE_META = {
  vetclinic:      { labelKey: 'vetclinic',          fallback: 'العيادة' },
  farrier:        { labelKey: 'farrier',            fallback: 'البيطار' },
  transportation: { labelKey: 'transport',          fallback: 'النقل' },
  accommodation:  { labelKey: 'accommodation',      fallback: 'السكن' },
};

const moneyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const smallMoneyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const intFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

const fmtInt = (value) => intFormatter.format(Number(value || 0));

function fmtMoney(value) {
  const n = Number(value || 0);
  if (Math.abs(n) < 1000 && !Number.isInteger(n)) {
    return smallMoneyFormatter.format(n);
  }
  return moneyFormatter.format(Math.round(n));
}
function fmtCompactMoney(value, t) {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${smallMoneyFormatter.format(n / 1000000)} ${t('million_short') || 'M'}`;
  if (abs >= 100000)  return `${smallMoneyFormatter.format(n / 1000)} ${t('thousand_short') || 'K'}`;
  return fmtMoney(n);
}
const currencyLabel = (t) => t('currency_sar') || 'SAR';
const toYMD = (d) => formatDateInput(d);
const toDateOnly = (v) => (v ? formatDateDisplay(v, '') : '');

function getPresetRange(preset) {
  const now = new Date();
  const today = toYMD(now);
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'last30') {
    const from = new Date(now);
    from.setDate(from.getDate() - 29);
    return { from: toYMD(from), to: today };
  }
  if (preset === 'year') {
    return { from: formatDateInput(new Date(now.getFullYear(), 0, 1)), to: today };
  }
  return {
    from: formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: today,
  };
}

function getServiceLabel(t, service) {
  const meta = SERVICE_META[service];
  return t(meta?.labelKey || service) || meta?.fallback || service;
}

// ---------------------------------------------------------------------------
// Horse field formatters (per-horse table in owner mode)
// ---------------------------------------------------------------------------

// Localised breed label. We pick the Arabic name when the current i18n
// language is Arabic, otherwise the English one — with the opposite as
// fallback so we always render something useful.
function formatBreed(horse, isArabic) {
  const ar = horse?.breed_arabic || '';
  const en = horse?.breed_english || '';
  if (isArabic) return ar || en || '-';
  return en || ar || '-';
}

// Compute age in whole years from date_of_birth; under one year we fall
// back to months so newborn foals still display a meaningful value.
function formatAge(horse, t) {
  const dob = horse?.date_of_birth;
  if (!dob) return '-';
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return '-';
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  const monthDelta = now.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < birth.getDate())) {
    years -= 1;
  }
  if (years < 0) return '-';
  if (years === 0) {
    const months = Math.max(
      0,
      (now.getFullYear() - birth.getFullYear()) * 12 +
        (now.getMonth() - birth.getMonth())
    );
    return `${months} ${t?.('months_short') || 'mo'}`;
  }
  return `${years} ${t?.('years_short') || 'yr'}`;
}

// Map gender codes used across the system to a human label:
//   S → Stallion (حصان)
//   M → Mare     (فرس)
//   G → Gelding  (مخصي)
//   C → Colt     (مهر)
//   F → Filly    (مهرة)
// Anything unknown falls through to the raw value so we never lose data.
function formatGender(horse, t) {
  // Note: `stallion` is unfortunately a nested object key in translation.json
  // (it holds form-field sub-labels), so calling t('stallion') would return
  // the object and trigger an i18next warning. We use the disambiguated
  // `horse_stallion` key instead. The other codes are top-level strings.
  const raw = String(horse?.gender || '').toUpperCase().trim();
  switch (raw) {
    case 'S':
    case 'STALLION':
      return t?.('horse_stallion') || 'حصان';
    case 'M':
    case 'MARE':
      return t?.('mare') || 'فرس';
    case 'G':
    case 'GELDING':
      return t?.('gelding') || 'مخصي';
    case 'C':
    case 'COLT':
      return t?.('colt') || 'مهر';
    case 'F':
    case 'FILLY':
      return t?.('filly') || 'مهرة';
    default:
      return raw || '-';
  }
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------
function ResponsiveGrid({ columns = 3, children }) {
  const mediumColumns = columns >= 5 ? 3 : columns;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: columns >= 4 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
          md: `repeat(${mediumColumns}, minmax(0, 1fr))`,
          lg: `repeat(${columns}, minmax(0, 1fr))`,
        },
        gap: 2,
        alignItems: 'stretch',
      }}
    >
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function FinanceEntitySummaryPage() {
  const { t, i18n } = useTranslation();
  const isArabic = String(i18n?.language || '').toLowerCase().startsWith('ar');

  const [scope, setScope] = useState('horse'); // 'horse' | 'owner'
  const [horseOptions, setHorseOptions] = useState([]);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [preset, setPreset] = useState('month');
  const [range, setRange] = useState(() => getPresetRange('month'));

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Fetch horse + owner lists once for the dropdowns.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setOptionsLoading(true);
      try {
        const [horsesRes, ownersRes] = await Promise.all([
          api.get('/horses').catch(() => ({ data: [] })),
          api.get('/owners').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const horses = (Array.isArray(horsesRes.data) ? horsesRes.data : [])
          .map((h) => ({
            id: h.horse_id ?? h.id,
            label: h.name_arabic || h.name || `#${h.horse_id}`,
            secondary: h.owner_name || '',
            rfid: h.rfid_chip_number || '',
          }))
          .filter((h) => h.id);
        const owners = (Array.isArray(ownersRes.data) ? ownersRes.data : [])
          .map((o) => ({
            id: o.owner_id ?? o.id,
            label: `${o.first_name || ''} ${o.last_name || ''}`.trim() || `#${o.owner_id}`,
            secondary: o.phone || o.email || '',
            horsesCount: Number(o.horses_count || 0),
          }))
          .filter((o) => o.id);
        setHorseOptions(horses);
        setOwnerOptions(owners);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // The active entity ID we'll send to the backend.
  const entityParams = useMemo(() => {
    if (scope === 'horse' && selectedHorse?.id) return { horse_id: selectedHorse.id };
    if (scope === 'owner' && selectedOwner?.id) return { owner_id: selectedOwner.id };
    return null;
  }, [scope, selectedHorse, selectedOwner]);

  const fetchSummary = useCallback(async () => {
    if (!entityParams) {
      setSummary(null);
      setErrorMsg('');
      return;
    }
    if (!range.from || !range.to) {
      setErrorMsg(t('invalid_date_range') || 'الفترة غير مكتملة');
      return;
    }
    if (range.from > range.to) {
      setErrorMsg(t('invalid_date_range') || 'تاريخ البداية بعد تاريخ النهاية');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    try {
      const { data } = await api.get('/accounting/finance/entity-totals', {
        params: { from: range.from, to: range.to, ...entityParams },
      });
      setSummary(data || null);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load entity finance summary';
      setErrorMsg(String(msg));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [range, entityParams, t]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const totals       = useMemo(() => Array.isArray(summary?.totals) ? summary.totals : [], [summary]);
  const grandTotal   = Number(summary?.grandTotal || 0);
  const recordsCount = Number(summary?.recordsCount || 0);
  const competition  = useMemo(() => Array.isArray(summary?.competitionRows) ? summary.competitionRows : [], [summary]);
  const horsesList   = useMemo(() => Array.isArray(summary?.horses) ? summary.horses : [], [summary]);
  const ownerInfo    = summary?.owner || null;

  // Net competition result (stable-included income – stable-included expense).
  // Direction values from the DB are uppercase strings 'REVENUE' / 'EXPENSE'.
  // We normalize them to lowercase before classifying so any future variants
  // (case differences, 'OUT'/'IN', etc.) still slot in correctly.
  const competitionAgg = useMemo(() => {
    let expenses = 0;
    let revenue  = 0;
    let excluded = 0;
    competition.forEach((row) => {
      const amount = Number(row.amount || 0);
      const included = Number(row.include_in_stable_finance) === 1;
      const direction = String(row.direction || '').toLowerCase();
      if (!included) {
        excluded += amount;
        return;
      }
      if (direction === 'expense' || direction === 'out' || direction === 'debit') {
        expenses += amount;
      } else {
        // Default bucket: REVENUE / income / credit / unknown → counted as revenue.
        revenue += amount;
      }
    });
    return {
      expenses,
      revenue,
      net: revenue - expenses,   // bug fix: was `revenue - excluded` which mixed scopes
      excluded,
    };
  }, [competition]);

  const handlePresetChange = (event) => {
    const next = event.target.value;
    setPreset(next);
    setRange(getPresetRange(next));
  };
  const handleRangeChange = (next) => { setPreset('custom'); setRange(next); };

  const handleScopeChange = (_e, value) => {
    if (!value) return;
    setScope(value);
    // Clear the opposite selection so it doesn't auto-trigger.
    if (value === 'horse') setSelectedOwner(null);
    else setSelectedHorse(null);
  };

  // -------------------------------------------------------------------
  // Excel export — premium formatting
  // -------------------------------------------------------------------
  // The output mirrors the project's gold identity (`#b79b5b`) and follows a
  // consistent visual rhythm across the 1-3 generated sheets:
  //   - row 1: gold banner with the report title (height 28)
  //   - row 2: subtitle / entity name (height 22)
  //   - row 3: period range
  //   - blank
  //   - section header (gold-soft fill, bold)
  //   - table header (gold fill, white text, bold, height 22)
  //   - data rows (alternating fills, thin borders, currency formatted)
  //   - footer (italic muted text with generation timestamp)
  //
  // All cell styling requires `cellStyles: true` on the writer, which we set.
  const exportToExcel = async () => {
    if (!summary) return;

    // -- Brand palette (hex without #, matches smartStableTheme.js) --
    // `COLOR_CARD` (#FFFDF8) is the base card fill; we don't apply it
    // explicitly because the default Excel cell background already maps to
    // white. Keeping the comment for parity with the theme reference.
    const COLOR_GOLD       = 'B79B5B';
    const COLOR_GOLD_SOFT  = 'EFE4CB';
    const COLOR_CARD_SOFT  = 'F7F3EA';
    const COLOR_LINE       = 'EADFCA';
    const COLOR_TEXT       = '4F545C';
    const COLOR_MUTED      = '7A7F87';
    const COLOR_GREEN_SOFT = 'EDF5EE';
    const COLOR_ROSE_SOFT  = 'F6ECEA';

    // -- Reusable style presets --
    const thinBorder = {
      top:    { style: 'thin', color: { rgb: COLOR_LINE } },
      bottom: { style: 'thin', color: { rgb: COLOR_LINE } },
      left:   { style: 'thin', color: { rgb: COLOR_LINE } },
      right:  { style: 'thin', color: { rgb: COLOR_LINE } },
    };
    const banner = {
      fill:      { fgColor: { rgb: COLOR_GOLD } },
      font:      { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    };
    const subtitle = {
      fill:      { fgColor: { rgb: COLOR_CARD_SOFT } },
      font:      { bold: true, sz: 12, color: { rgb: COLOR_TEXT } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border:    thinBorder,
    };
    const sectionHeader = {
      fill:      { fgColor: { rgb: COLOR_GOLD_SOFT } },
      font:      { bold: true, sz: 11, color: { rgb: COLOR_TEXT } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border:    thinBorder,
    };
    const tableHeader = {
      fill:      { fgColor: { rgb: COLOR_GOLD } },
      font:      { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border:    thinBorder,
    };
    const cellBase = {
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border:    thinBorder,
      font:      { sz: 11, color: { rgb: COLOR_TEXT } },
    };
    const cellAlt = {
      ...cellBase,
      fill: { fgColor: { rgb: COLOR_CARD_SOFT } },
    };
    const cellMoney = (alt = false) => ({
      ...(alt ? cellAlt : cellBase),
      font: { sz: 11, bold: true, color: { rgb: COLOR_TEXT } },
      numFmt: '#,##0.00',
    });
    const cellLabel = {
      ...cellBase,
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true, indent: 1 },
      font: { sz: 11, bold: true, color: { rgb: COLOR_TEXT } },
    };
    const cellValue = {
      ...cellBase,
      font: { sz: 11, color: { rgb: COLOR_TEXT } },
    };
    const totalsRow = {
      ...cellBase,
      fill: { fgColor: { rgb: COLOR_GOLD_SOFT } },
      font: { sz: 11, bold: true, color: { rgb: COLOR_TEXT } },
      numFmt: '#,##0.00',
    };
    const footerStyle = {
      alignment: { horizontal: 'center', vertical: 'center' },
      font: { italic: true, sz: 9, color: { rgb: COLOR_MUTED } },
    };

    // -- ExcelJS adapter (migrated from xlsx-js-style) --
    const colLetter = (n) => {
      let s = '';
      let num = n;
      while (num > 0) { const rem = (num - 1) % 26; s = String.fromCharCode(65 + rem) + s; num = Math.floor((num - 1) / 26); }
      return s;
    };
    const cellRef = ({ r, c }) => `${colLetter(c + 1)}${r + 1}`;
    const A = (rgb) => ({ argb: `FF${rgb}` });
    const side = (b) => (b ? { style: b.style, color: b.color?.rgb ? A(b.color.rgb) : undefined } : undefined);
    // يحوّل كائن نمط xlsx-js-style إلى نمط خلية ExcelJS.
    const toCellStyle = (s) => {
      if (!s) return {};
      const out = {};
      if (s.font) out.font = { bold: s.font.bold, italic: s.font.italic, size: s.font.sz, color: s.font.color?.rgb ? A(s.font.color.rgb) : undefined };
      if (s.fill?.fgColor?.rgb) out.fill = { type: 'pattern', pattern: 'solid', fgColor: A(s.fill.fgColor.rgb) };
      if (s.alignment) out.alignment = { ...s.alignment, vertical: s.alignment.vertical === 'center' ? 'middle' : s.alignment.vertical };
      if (s.border) out.border = { top: side(s.border.top), bottom: side(s.border.bottom), left: side(s.border.left), right: side(s.border.right) };
      if (s.numFmt) out.numFmt = s.numFmt;
      return out;
    };
    const makeSheet = (workbook, name, aoa) => {
      const ws = workbook.addWorksheet(name);
      aoa.forEach((row) => ws.addRow(row));
      return ws;
    };
    const setColWidths = (ws, widths) => widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    const setRowHeights = (ws, heights) => heights.forEach((h, i) => { ws.getRow(i + 1).height = h; });
    const mergeRanges = (ws, merges) => merges.forEach((m) => ws.mergeCells(`${cellRef(m.s)}:${cellRef(m.e)}`));
    const applyStyle = (ws, ref, style) => { ws.getCell(ref).style = toCellStyle(style); };

    const wb = new ExcelJS.Workbook();
    const currency = currencyLabel(t);
    const entityLabel = scope === 'horse'
      ? (selectedHorse?.label || '-')
      : (selectedOwner?.label || ownerInfo?.owner_name || '-');
    const scopeLabel = scope === 'horse'
      ? (t('horse_singular') || 'Horse')
      : (t('owner') || 'Owner');
    const generatedAt = new Date().toLocaleString();

    // ===================================================================
    // Sheet 1: Summary
    // ===================================================================
    const TOTAL_COLS = 4;
    const sumRows = [];

    // r0: banner title
    sumRows.push([t('entity_finance_summary') || 'Entity finance summary', '', '', '']);
    // r1: entity name
    sumRows.push([`${scopeLabel}: ${entityLabel}`, '', '', '']);
    // r2: period
    sumRows.push([`${t('period') || 'Period'}: ${range.from} → ${range.to}`, '', '', '']);
    // r3: blank
    sumRows.push(['', '', '', '']);
    // r4: section — info
    sumRows.push([t('details') || 'Details', '', '', '']);
    // r5..: identity rows (label | value | blank | blank)
    sumRows.push([
      t('owner') || 'Owner',
      scope === 'horse' ? (ownerInfo?.owner_name || '-') : entityLabel,
      '',
      '',
    ]);
    sumRows.push([
      t('horses') || 'Horses',
      scope === 'owner' ? horsesList.length : 1,
      '',
      '',
    ]);
    sumRows.push([
      t('records_count') || 'Records',
      recordsCount,
      '',
      '',
    ]);
    sumRows.push([
      t('total_revenue') || 'Total revenue',
      Number(grandTotal || 0),
      currency,
      '',
    ]);
    // blank
    sumRows.push(['', '', '', '']);
    // section — service breakdown
    sumRows.push([t('service_breakdown') || 'Service breakdown', '', '', '']);
    // table header
    sumRows.push([
      t('service') || 'Service',
      `${t('revenue') || 'Revenue'} (${currency})`,
      t('records') || 'Records',
      t('share') || 'Share',
    ]);
    // service rows
    const serviceStart = sumRows.length;
    totals.forEach((row) => {
      const share = grandTotal > 0 ? ((row.revenue / grandTotal) * 100) / 100 : 0;
      sumRows.push([
        getServiceLabel(t, row.service),
        Number(row.revenue || 0),
        Number(row.records_count || 0),
        share, // stored as 0..1 fraction, formatted as % below
      ]);
    });
    const serviceEnd = sumRows.length - 1;
    // totals row
    sumRows.push([
      t('grand_total') || 'Grand total',
      Number(grandTotal || 0),
      Number(recordsCount || 0),
      grandTotal > 0 ? 1 : 0,
    ]);
    const grandTotalRow = sumRows.length - 1;
    // blank
    sumRows.push(['', '', '', '']);
    // section — competition
    sumRows.push([t('competition_finance') || 'Competition finance', '', '', '']);
    sumRows.push([
      t('competition_expenses') || 'Competition expenses',
      Number(competitionAgg.expenses || 0),
      currency,
      '',
    ]);
    sumRows.push([
      t('competition_revenue') || 'Competition revenue',
      Number(competitionAgg.revenue || 0),
      currency,
      '',
    ]);
    sumRows.push([
      t('net_competition_result') || 'Net competition',
      Number(competitionAgg.net || 0),
      currency,
      '',
    ]);
    // blank + footer
    sumRows.push(['', '', '', '']);
    sumRows.push([`${t('generated_at') || 'Generated at'}: ${generatedAt}`, '', '', '']);

    const wsSummary = makeSheet(wb, (t('summary') || 'Summary').slice(0, 31), sumRows);
    setColWidths(wsSummary, [32, 22, 14, 14]);
    setRowHeights(wsSummary, sumRows.map((_, idx) => (idx === 0 ? 30 : idx === 1 ? 22 : 20)));
    mergeRanges(wsSummary, [
      { s: { r: 0, c: 0 }, e: { r: 0, c: TOTAL_COLS - 1 } },     // banner
      { s: { r: 1, c: 0 }, e: { r: 1, c: TOTAL_COLS - 1 } },     // entity
      { s: { r: 2, c: 0 }, e: { r: 2, c: TOTAL_COLS - 1 } },     // period
      { s: { r: 4, c: 0 }, e: { r: 4, c: TOTAL_COLS - 1 } },     // section: details
      { s: { r: 10, c: 0 }, e: { r: 10, c: TOTAL_COLS - 1 } },   // section: service breakdown
      { s: { r: serviceEnd + 3, c: 0 }, e: { r: serviceEnd + 3, c: TOTAL_COLS - 1 } }, // section: competition
      { s: { r: sumRows.length - 1, c: 0 }, e: { r: sumRows.length - 1, c: TOTAL_COLS - 1 } }, // footer
    ]);
    wsSummary.views = [{ state: 'frozen', ySplit: 1 }];
    wsSummary.pageSetup = { paperSize: 9, orientation: 'portrait', fitToWidth: 1, fitToHeight: 0 };

    // -- Style the summary sheet cell-by-cell --
    applyStyle(wsSummary, 'A1', banner);
    applyStyle(wsSummary, 'A2', subtitle);
    applyStyle(wsSummary, 'A3', subtitle);
    applyStyle(wsSummary, 'A5', sectionHeader);
    applyStyle(wsSummary, 'A11', sectionHeader);
    applyStyle(wsSummary, `A${serviceEnd + 4}`, sectionHeader);
    applyStyle(wsSummary, `A${sumRows.length}`, footerStyle);

    // Identity rows (label / value) → rows 6..9 (1-indexed)
    for (let r = 5; r <= 8; r += 1) {
      applyStyle(wsSummary, `A${r + 1}`, cellLabel);
      applyStyle(wsSummary, `B${r + 1}`, r === 8 ? cellMoney(false) : cellValue);
      applyStyle(wsSummary, `C${r + 1}`, cellValue);
      applyStyle(wsSummary, `D${r + 1}`, cellValue);
    }

    // Service-breakdown table header → row 12 (1-indexed)
    for (let c = 0; c < TOTAL_COLS; c += 1) {
      applyStyle(wsSummary, cellRef({ r: 11, c }), tableHeader);
    }
    // Service rows + grand total — alternate row fills + currency formatting
    for (let r = serviceStart; r <= serviceEnd; r += 1) {
      const alt = (r - serviceStart) % 2 === 1;
      applyStyle(wsSummary, cellRef({ r, c: 0 }), alt ? cellAlt : cellBase);
      applyStyle(wsSummary, cellRef({ r, c: 1 }), cellMoney(alt));
      applyStyle(wsSummary, cellRef({ r, c: 2 }), alt ? cellAlt : cellBase);
      wsSummary.getCell(cellRef({ r, c: 3 })).style = toCellStyle({ ...(alt ? cellAlt : cellBase), numFmt: '0.0%' });
    }
    // Grand total row
    for (let c = 0; c < TOTAL_COLS; c += 1) {
      const ref = cellRef({ r: grandTotalRow, c });
      applyStyle(wsSummary, ref, c === 3 ? { ...totalsRow, numFmt: '0.0%' } : totalsRow);
    }

    // Competition KPI rows
    for (let r = serviceEnd + 4; r < sumRows.length - 2; r += 1) {
      applyStyle(wsSummary, `A${r + 1}`, cellLabel);
      const bRef = `B${r + 1}`;
      applyStyle(wsSummary, bRef, cellMoney(false));
      // colour the net row by sign
      if (r === serviceEnd + 6) {
        const bCell = wsSummary.getCell(bRef);
        const netVal = Number(bCell.value || 0);
        const fillRgb = netVal < 0 ? COLOR_ROSE_SOFT : COLOR_GREEN_SOFT;
        bCell.style = toCellStyle({ ...cellMoney(false), fill: { fgColor: { rgb: fillRgb } } });
      }
      applyStyle(wsSummary, `C${r + 1}`, cellValue);
      applyStyle(wsSummary, `D${r + 1}`, cellValue);
    }

    // (ورقة الملخّص أُضيفت إلى المصنّف عبر makeSheet)

    // ===================================================================
    // Sheet 2: Competition
    // ===================================================================
    if (competition.length > 0) {
      const compHeader = [
        t('date') || 'Date',
        t('competition') || 'Competition',
        t('horse_singular') || 'Horse',
        t('owner') || 'Owner',
        t('transaction_type') || 'Type',
        t('direction') || 'Direction',
        `${t('amount') || 'Amount'} (${currency})`,
        t('included_in_stable_finance') || 'Included',
      ];
      const compTotal = competition.reduce((s, row) => {
        const amt = Number(row.amount || 0);
        const dir = String(row.direction || '').toLowerCase();
        const inc = Number(row.include_in_stable_finance) === 1;
        if (!inc) return s;
        return dir === 'expense' || dir === 'out' || dir === 'debit' ? s - amt : s + amt;
      }, 0);

      const compAoA = [
        [t('competition_finance') || 'Competition finance', '', '', '', '', '', '', ''],
        [`${scopeLabel}: ${entityLabel} · ${range.from} → ${range.to}`, '', '', '', '', '', '', ''],
        compHeader,
        ...competition.map((row) => [
          toDateOnly(row.transaction_date),
          row.competition_name || row.competition_name_ar || '-',
          row.horse_name || '-',
          row.owner_name || '-',
          row.transaction_type || '-',
          row.direction || '-',
          Number(row.amount || 0),
          Number(row.include_in_stable_finance) ? (t('yes') || 'Yes') : (t('no') || 'No'),
        ]),
        [
          t('net_competition_result') || 'Net',
          '', '', '', '', '',
          Number(compTotal),
          '',
        ],
        ['', '', '', '', '', '', '', ''],
        [`${t('generated_at') || 'Generated at'}: ${generatedAt}`, '', '', '', '', '', '', ''],
      ];
      const COLS = compHeader.length;
      const wsComp = makeSheet(wb, (t('competition') || 'Competition').slice(0, 31), compAoA);
      setColWidths(wsComp, [13, 28, 22, 22, 16, 12, 16, 12]);
      setRowHeights(wsComp, compAoA.map((_, idx) => (idx === 0 ? 30 : idx === 1 ? 22 : 20)));
      mergeRanges(wsComp, [
        { s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: COLS - 1 } },
        { s: { r: compAoA.length - 1, c: 0 }, e: { r: compAoA.length - 1, c: COLS - 1 } },
        { s: { r: compAoA.length - 3, c: 0 }, e: { r: compAoA.length - 3, c: COLS - 2 } },
      ]);
      wsComp.views = [{ state: 'frozen', ySplit: 3 }];
      wsComp.autoFilter = `A3:${cellRef({ r: compAoA.length - 4, c: COLS - 1 })}`;

      applyStyle(wsComp, 'A1', banner);
      applyStyle(wsComp, 'A2', subtitle);
      for (let c = 0; c < COLS; c += 1) {
        applyStyle(wsComp, cellRef({ r: 2, c }), tableHeader);
      }
      const dataStart = 3;
      const dataEnd = dataStart + competition.length - 1;
      for (let r = dataStart; r <= dataEnd; r += 1) {
        const alt = (r - dataStart) % 2 === 1;
        for (let c = 0; c < COLS; c += 1) {
          const ref = cellRef({ r, c });
          if (c === 6) {
            applyStyle(wsComp, ref, cellMoney(alt));
          } else {
            applyStyle(wsComp, ref, alt ? cellAlt : cellBase);
          }
        }
      }
      // Net total row
      const totalRowIdx = dataEnd + 1;
      for (let c = 0; c < COLS; c += 1) {
        const ref = cellRef({ r: totalRowIdx, c });
        applyStyle(wsComp, ref, totalsRow);
      }
      applyStyle(wsComp, `A${compAoA.length}`, footerStyle);

      // (ورقة البطولات أُضيفت إلى المصنّف عبر makeSheet)
    }

    // ===================================================================
    // Sheet 3: Owner horses
    // ===================================================================
    if (scope === 'owner' && horsesList.length > 0) {
      const horseHeader = [
        '#',
        t('horse_singular') || 'Horse',
        t('breed') || 'Breed',
        t('age') || 'Age',
        t('gender') || 'Gender',
        `${t('total_expenses') || 'Total expenses'} (${currency})`,
      ];
      const sorted = horsesList
        .slice()
        .sort((a, b) => Number(b.total_spend || 0) - Number(a.total_spend || 0));
      const grandSpend = sorted.reduce((s, h) => s + Number(h.total_spend || 0), 0);

      const horseAoA = [
        [t('owner_horses') || 'Owner horses', '', '', '', '', ''],
        [`${scopeLabel}: ${entityLabel} · ${range.from} → ${range.to}`, '', '', '', '', ''],
        horseHeader,
        ...sorted.map((h, idx) => [
          idx + 1,
          h.horse_name || '-',
          formatBreed(h, isArabic),
          formatAge(h, t),
          formatGender(h, t),
          Number(h.total_spend || 0),
        ]),
        [
          t('grand_total') || 'Grand total',
          '', '', '', '',
          Number(grandSpend),
        ],
        ['', '', '', '', '', ''],
        [`${t('generated_at') || 'Generated at'}: ${generatedAt}`, '', '', '', '', ''],
      ];
      const COLS = horseHeader.length;
      const wsHorses = makeSheet(wb, (t('horses') || 'Horses').slice(0, 31), horseAoA);
      setColWidths(wsHorses, [6, 26, 20, 12, 14, 18]);
      setRowHeights(wsHorses, horseAoA.map((_, idx) => (idx === 0 ? 30 : idx === 1 ? 22 : 20)));
      mergeRanges(wsHorses, [
        { s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: COLS - 1 } },
        { s: { r: horseAoA.length - 1, c: 0 }, e: { r: horseAoA.length - 1, c: COLS - 1 } },
        { s: { r: horseAoA.length - 3, c: 0 }, e: { r: horseAoA.length - 3, c: COLS - 2 } },
      ]);
      wsHorses.views = [{ state: 'frozen', ySplit: 3 }];
      wsHorses.autoFilter = `A3:${cellRef({ r: horseAoA.length - 4, c: COLS - 1 })}`;

      applyStyle(wsHorses, 'A1', banner);
      applyStyle(wsHorses, 'A2', subtitle);
      for (let c = 0; c < COLS; c += 1) {
        applyStyle(wsHorses, cellRef({ r: 2, c }), tableHeader);
      }
      const dataStart = 3;
      const dataEnd = dataStart + sorted.length - 1;
      for (let r = dataStart; r <= dataEnd; r += 1) {
        const alt = (r - dataStart) % 2 === 1;
        for (let c = 0; c < COLS; c += 1) {
          const ref = cellRef({ r, c });
          if (c === 5) {
            applyStyle(wsHorses, ref, cellMoney(alt));
          } else {
            applyStyle(wsHorses, ref, alt ? cellAlt : cellBase);
          }
        }
      }
      // Grand total row
      const totalRowIdx = dataEnd + 1;
      for (let c = 0; c < COLS; c += 1) {
        applyStyle(wsHorses, cellRef({ r: totalRowIdx, c }), totalsRow);
      }
      applyStyle(wsHorses, `A${horseAoA.length}`, footerStyle);

      // (ورقة خيل المالك أُضيفت إلى المصنّف عبر makeSheet)
    }

    // ===================================================================
    // Save the workbook
    // ===================================================================
    const excelBuffer = await wb.xlsx.writeBuffer();
    const stamp = new Date().toISOString().slice(0, 10);
    const safeEntity = String(entityLabel)
      .replace(/[\\/:*?"<>|]/g, '_')
      .slice(0, 40);
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `Finance_${scope}_${safeEntity}_${stamp}.xlsx`
    );
  };

  // -------- Render --------
  return (
    <Box
      className="smart-page-container"
      sx={{
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        bgcolor: COLORS.page,
        overflowX: 'hidden',
      }}
    >
      <Stack spacing={2}>
        {/* ---------- Header ---------- */}
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 1.75, md: 2 },
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ gap: 2 }}
          >
            <Box sx={{ minWidth: 240 }}>
              <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('entity_finance_summary') || 'ملخص مالي لخيل / مالك'}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted }}>
                {t('entity_finance_summary_hint')
                  || 'تقرير مالي مفصل على مستوى خيل واحد أو مالك واحد ضمن الفترة المحددة'}
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                select
                size="small"
                label={t('period') || 'الفترة'}
                value={preset}
                onChange={handlePresetChange}
                sx={{ minWidth: { xs: '100%', sm: 170 } }}
              >
                <MenuItem value="today">{t('today') || 'اليوم'}</MenuItem>
                <MenuItem value="month">{t('current_month') || 'الشهر الحالي'}</MenuItem>
                <MenuItem value="last30">{t('last_30_days') || 'آخر 30 يوم'}</MenuItem>
                <MenuItem value="year">{t('current_year') || 'السنة الحالية'}</MenuItem>
                <MenuItem value="custom">{t('custom') || 'مخصص'}</MenuItem>
              </TextField>

              <DateRange value={range} onChange={handleRangeChange} t={t} />

              {/* Export the whole report (summary + competition + per-horse
                  breakdown) to a styled multi-sheet XLSX. Disabled until the
                  user picks an entity and the data loads. */}
              <Tooltip title={t('export_report_excel') || 'تصدير Excel'}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    disabled={loading || !summary}
                    startIcon={<FileDownloadIcon />}
                    sx={{
                      height: 40,
                      whiteSpace: 'nowrap',
                      borderColor: COLORS.line,
                      color: COLORS.gold,
                      fontWeight: 800,
                      '&:hover': {
                        borderColor: COLORS.gold,
                        bgcolor: COLORS.cardSoft,
                      },
                    }}
                  >
                    {t('export_excel') || 'Export Excel'}
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title={t('refresh') || 'تحديث'}>
                <span>
                  <Button
                    variant="contained"
                    onClick={fetchSummary}
                    disabled={loading || !entityParams}
                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    sx={{
                      height: 40,
                      whiteSpace: 'nowrap',
                      bgcolor: COLORS.gold,
                      fontWeight: 900,
                      '&:hover': { bgcolor: COLORS.goldHover },
                    }}
                  >
                    {t('refresh') || 'تحديث'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {/* ---------- Entity picker ---------- */}
        <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <ToggleButtonGroup
              value={scope}
              exclusive
              onChange={handleScopeChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: COLORS.text,
                  borderColor: COLORS.line,
                  fontWeight: 800,
                  '&.Mui-selected': {
                    bgcolor: COLORS.gold,
                    color: '#fff',
                    '&:hover': { bgcolor: COLORS.goldHover },
                  },
                },
              }}
            >
              <ToggleButton value="horse">
                <PetsIcon fontSize="small" sx={{ mr: 0.75 }} />
                {t('horse_singular') || 'الخيل'}
              </ToggleButton>
              <ToggleButton value="owner">
                <PersonIcon fontSize="small" sx={{ mr: 0.75 }} />
                {t('owner') || 'المالك'}
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {scope === 'horse' ? (
                <Autocomplete
                  size="small"
                  loading={optionsLoading}
                  options={horseOptions}
                  value={selectedHorse}
                  onChange={(_e, value) => setSelectedHorse(value)}
                  getOptionLabel={(opt) => opt?.label || ''}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: COLORS.text }}>{option.label}</Typography>
                        <Typography variant="caption" sx={{ color: COLORS.muted }}>
                          {option.secondary || '-'}{option.rfid ? ` · RFID ${option.rfid}` : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('select_horse') || 'اختر الخيل'}
                      placeholder={t('search') || 'بحث'}
                    />
                  )}
                />
              ) : (
                <Autocomplete
                  size="small"
                  loading={optionsLoading}
                  options={ownerOptions}
                  value={selectedOwner}
                  onChange={(_e, value) => setSelectedOwner(value)}
                  getOptionLabel={(opt) => opt?.label || ''}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: COLORS.text }}>{option.label}</Typography>
                        <Typography variant="caption" sx={{ color: COLORS.muted }}>
                          {option.secondary || '-'}{option.horsesCount ? ` · ${option.horsesCount} ${t('horses') || 'خيول'}` : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('select_owner') || 'اختر المالك'}
                      placeholder={t('search') || 'بحث'}
                    />
                  )}
                />
              )}
            </Box>
          </Stack>
        </Paper>

        {/* ---------- Empty / Error state ---------- */}
        {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
        {!entityParams && !errorMsg ? (
          <Alert severity="info" sx={{ border: `1px solid ${COLORS.line}`, bgcolor: COLORS.card }}>
            {t('pick_entity_to_see_summary')
              || 'اختر خيلاً أو مالكاً من الأعلى لعرض الملخص المالي'}
          </Alert>
        ) : null}

        {/* ---------- Identity card (selected entity) ---------- */}
        {summary && (
          <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Box>
                <Typography variant="overline" sx={{ color: COLORS.gold, fontWeight: 900 }}>
                  {scope === 'horse' ? (t('horse_singular') || 'الخيل') : (t('owner') || 'المالك')}
                </Typography>
                <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                  {scope === 'horse'
                    ? (selectedHorse?.label || '-')
                    : (selectedOwner?.label || ownerInfo?.owner_name || '-')}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.muted }}>
                  {scope === 'horse' && ownerInfo
                    ? `${t('owner') || 'المالك'}: ${ownerInfo.owner_name || '-'}`
                    : null}
                  {scope === 'owner'
                    ? `${horsesList.length} ${t('horses') || 'خيول'}`
                    : null}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`${t('from') || 'من'}: ${range.from}`} sx={{ bgcolor: COLORS.goldSoft }} />
                <Chip label={`${t('to') || 'إلى'}: ${range.to}`} sx={{ bgcolor: COLORS.goldSoft }} />
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* ---------- KPIs ---------- */}
        {summary && (
          <ResponsiveGrid columns={3}>
            <KPI
              title={t('total_revenue') || 'إجمالي الإيرادات'}
              value={`${fmtCompactMoney(grandTotal, t)} ${currencyLabel(t)}`}
            />
            <KPI
              title={t('records_count') || 'عدد العمليات'}
              value={fmtInt(recordsCount)}
            />
            <KPI
              title={scope === 'horse' ? (t('owner') || 'المالك') : (t('horses') || 'الخيول')}
              value={scope === 'horse' ? (ownerInfo?.owner_name || '-') : fmtInt(horsesList.length)}
            />
          </ResponsiveGrid>
        )}

        {/* ---------- Service breakdown cards ---------- */}
        {summary && (
          <ResponsiveGrid columns={4}>
            {totals.map((row) => {
              const share = grandTotal > 0 ? (row.revenue / grandTotal) * 100 : 0;
              return (
                <Paper
                  key={row.service}
                  elevation={0}
                  sx={{ p: 2.25, ...cardSx, height: '100%', minHeight: 132 }}
                >
                  <Stack spacing={1.25} sx={{ height: '100%' }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                        {getServiceLabel(t, row.service)}
                      </Typography>
                      <TrendingUpIcon fontSize="small" color="action" />
                    </Stack>
                    <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                      {fmtMoney(row.revenue)} {currencyLabel(t)}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        size="small"
                        icon={<ReceiptLongIcon />}
                        label={`${fmtInt(row.records_count)} ${t('records') || 'عملية'}`}
                      />
                      <Chip size="small" label={`${share.toFixed(1)}%`} />
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </ResponsiveGrid>
        )}

        {/* ---------- Competition section ---------- */}
        {summary && (
          <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.75 }}>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('competition_finance') || 'Competition finance'}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted }}>
                {t('competition_finance_hint_entity')
                  || 'الحركات المالية الخاصة بالبطولات لهذا الكيان فقط'}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              <ResponsiveGrid columns={3}>
                <KPI
                  title={t('competition_expenses') || 'Competition expenses'}
                  value={`${fmtMoney(competitionAgg.expenses)} ${currencyLabel(t)}`}
                />
                <KPI
                  title={t('competition_revenue') || 'Competition revenue'}
                  value={`${fmtMoney(competitionAgg.revenue)} ${currencyLabel(t)}`}
                />
                <KPI
                  title={t('net_competition_result') || 'Net competition result'}
                  value={`${fmtMoney(competitionAgg.net)} ${currencyLabel(t)}`}
                />
              </ResponsiveGrid>

              <TableContainer sx={{ mt: 2, width: '100%' }}>
                <Table size="small" sx={{ minWidth: 980 }}>
                  <TableHead sx={smartTableHeadSx}>
                    <TableRow>
                      <TableCell align="center">{t('date') || 'Date'}</TableCell>
                      <TableCell align="center">{t('competition') || 'Competition'}</TableCell>
                      <TableCell align="center">{t('horse_singular') || 'Horse'}</TableCell>
                      <TableCell align="center">{t('owner') || 'Owner'}</TableCell>
                      <TableCell align="center">{t('transaction_type') || 'Type'}</TableCell>
                      <TableCell align="center">{t('direction') || 'Direction'}</TableCell>
                      <TableCell align="center">{t('amount') || 'Amount'}</TableCell>
                      <TableCell align="center">{t('included_in_stable_finance') || 'Included'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody
                    sx={{
                      // Every data cell centers its content so columns line up
                      // cleanly under their headers regardless of cell length.
                      '& .MuiTableCell-root': { textAlign: 'center' },
                    }}
                  >
                    {competition.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          {t('no_competition_financial_records_found')
                            || 'No competition financial records found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      competition.slice(0, 20).map((row) => (
                        <TableRow key={row.transaction_id} hover>
                          <TableCell align="center">
                            <span className="date-ltr">{toDateOnly(row.transaction_date)}</span>
                          </TableCell>
                          <TableCell align="center">{row.competition_name || row.competition_name_ar || '-'}</TableCell>
                          <TableCell align="center">{row.horse_name || '-'}</TableCell>
                          <TableCell align="center">{row.owner_name || '-'}</TableCell>
                          <TableCell align="center">{t(String(row.transaction_type || '').toLowerCase()) || row.transaction_type}</TableCell>
                          <TableCell align="center">{t(String(row.direction || '').toLowerCase()) || row.direction}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 800 }}>
                            {fmtMoney(row.amount)} {currencyLabel(t)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={Number(row.include_in_stable_finance) ? (t('yes') || 'Yes') : (t('no') || 'No')}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {competitionAgg.excluded > 0 ? (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: COLORS.muted }}>
                  {t('excluded_from_stable_finance') || 'Excluded from stable finance'}:{' '}
                  {fmtMoney(competitionAgg.excluded)} {currencyLabel(t)}
                </Typography>
              ) : null}
            </Box>
          </Paper>
        )}

        {/* ---------- Owner mode: per-horse list ---------- */}
        {summary && scope === 'owner' && horsesList.length > 0 && (
          <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.75 }}>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('owner_horses') || 'خيول المالك'}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted }}>
                {t('owner_horses_in_range')
                  || `${horsesList.length} خيل · المصاريف ضمن الفترة المحددة`}
              </Typography>
            </Box>
            <Divider />
            <TableContainer sx={{ width: '100%' }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead sx={smartTableHeadSx}>
                  <TableRow>
                    <TableCell align="center" sx={{ width: 60 }}>#</TableCell>
                    <TableCell align="center">{t('horse_singular') || 'الخيل'}</TableCell>
                    <TableCell align="center">{t('breed') || 'السلالة'}</TableCell>
                    <TableCell align="center" sx={{ width: 90 }}>
                      {t('age') || 'العمر'}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 110 }}>
                      {t('gender') || 'الجنس'}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 180 }}>
                      {t('total_expenses') || 'إجمالي المصاريف'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    '& .MuiTableCell-root': { textAlign: 'center' },
                  }}
                >
                  {horsesList
                    .slice()
                    .sort((a, b) => Number(b.total_spend || 0) - Number(a.total_spend || 0))
                    .map((h, idx) => (
                      <TableRow key={h.horse_id} hover>
                        <TableCell align="center">{idx + 1}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 800 }}>
                          {h.horse_name || '-'}
                        </TableCell>
                        <TableCell align="center">{formatBreed(h, isArabic)}</TableCell>
                        <TableCell align="center">{formatAge(h, t)}</TableCell>
                        <TableCell align="center">{formatGender(h, t)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 800 }}>
                          {fmtMoney(h.total_spend)} {currencyLabel(t)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
